import {
	ChangeDetectionStrategy,
	Component,
	inject,
	Inject,
	OnInit,
	AfterViewChecked,
} from '@angular/core';
import {
	FormBuilder,
	FormControl,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofActionDispatched, Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoginFailed } from '@state/auth/auth.actions';
import { envTypes } from 'src/environments/env.types';
import { environment } from 'src/environments/environment';
import { ICON } from '@core/util/icon';
import { LeapOnboardingDialogData } from '../../leap/entry-point/entry-point.types';
import { LeapOnboardingStatus } from '../../leap/leap-api.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { UserProfileState } from '@state/user-profile';
import {
	ILeapOnboardingService,
	LEAP_ONBOARDING_SERVICE,
} from '../../leap/leap-onboarding.models';

enum LoginScreen {
	CHOOSE_LOGIN_METHOD = 'CHOOSE_LOGIN_METHOD',
	USE_EXISTING_ACCOUNT = 'USE_EXISTING_ACCOUNT',
}

enum USER_CREDENTIALS {
	EMAIL = 'email',
	PASSWORD = 'password',
}

@UntilDestroy()
@Component({
	selector: 'leap-app-login',
	templateUrl: './login.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LeapLoginComponent implements OnInit, AfterViewChecked {
	@Select(AuthState.getLoading) authIsLoading$: Observable<boolean>;
	@Select(AuthState.loginChecked) loginChecked$: Observable<boolean>;
	@Select(AuthState.getEmail) loggedInUserEmail$: Observable<string>;

	@Select(UserProfileState.getCurrentBusinessId)
	currentBusinessId$: Observable<string>;

	LoginScreen = LoginScreen;
	currloginScreen = new BehaviorSubject<LoginScreen>(
		LoginScreen.CHOOSE_LOGIN_METHOD,
	);

	ICON = ICON;

	forgetUrl = UrlService.Forgot();

	registerUrl = UrlService.Register();

	contactUrl = UrlService.Contact();

	legalUrl = UrlService.Legal();

	USER_CREDENTIALS = USER_CREDENTIALS;

	authForm = this.fb.group(
		{
			[USER_CREDENTIALS.EMAIL]: this.fb.control(
				environment.env === envTypes.DEVELOPMENT
					? 'lawyer1@lawlabs.ca'
					: (localStorage.getItem('loginEmail') ?? ''),
				[Validators.required, Validators.email],
			),
			[USER_CREDENTIALS.PASSWORD]: this.fb.control(
				environment.env === envTypes.DEVELOPMENT ? 'abc123' : '',
				[Validators.required],
			),
		},
		{ updateOn: 'submit' },
	);

	public emailFormControl = this.authForm.get(USER_CREDENTIALS.EMAIL);
	public passwordFormControl = this.authForm.get(USER_CREDENTIALS.PASSWORD);
	public showEmailIsNotAvailableError$ = new BehaviorSubject(false);
	public userAccountBelongsToDifferentBusinessError$: BehaviorSubject<{
		hasError: boolean;
		invalidEmail: string;
	}>;
	public isLoading$: BehaviorSubject<boolean>;

	private onboardingService: ILeapOnboardingService = inject(
		LEAP_ONBOARDING_SERVICE,
	);

	constructor(
		private authDispatch: AuthDispatch,
		@Inject(MAT_DIALOG_DATA) public data: LeapOnboardingDialogData,
		private fb: FormBuilder,
		private actions$: Actions,
		private snackbarService: SnackbarService,
	) {}

	async ngOnInit() {
		// Start clean
		this.authDispatch.LogOut();

		this.userAccountBelongsToDifferentBusinessError$ =
			this.onboardingService.userAccountBelongsToDifferentBusinessError$;

		this.isLoading$ = this.onboardingService.isLoading$;

		this.isLoading$
			.pipe(untilDestroyed(this), distinctUntilChanged())
			.subscribe((loading) => {
				if (loading) {
					this.authForm.disable();
				} else {
					this.authForm.enable();
				}
			});

		this.emailFormControl.addValidators(
			this.getUserAccountBelongsToDifferentBusinessValidator(),
		);

		this.actions$
			.pipe(untilDestroyed(this), ofActionDispatched(LoginFailed))
			.subscribe(() => {
				this.isLoading$.next(false);
			});

		// set up event handlers to handle user-log-in event to complete onboarding
		await this.setupAuthStateChangeListenerForOnboarding();

		this.showEmailIsNotAvailableError$
			.pipe(untilDestroyed(this))
			.subscribe((showEmailError) => {
				if (showEmailError) {
					this.snackbarService.openSnackBar(
						{
							text: 'Account already exists!',
							type: 'error',
							icon: ICON.EXCLAMATION,
						},
						{ duration: 5000 },
					);
				}
			});

		this.userAccountBelongsToDifferentBusinessError$
			.pipe(untilDestroyed(this))
			.subscribe((accountBusinessMismatchError) => {
				if (accountBusinessMismatchError.hasError) {
					this.snackbarService.openSnackBar(
						{
							text: 'Closer account must match LEAP business account',
							type: 'error',
							icon: ICON.EXCLAMATION,
						},
						{ duration: 5000 },
					);
				}
			});
	}

	ngAfterViewChecked(): void {
		// temp solution: override global styling but only in this component, investigate making changes to the shared components with Nick
		(
			document.querySelector('.mdc-dialog__surface') as HTMLElement
		).style.padding = '0';
		(document.querySelector('footer') as HTMLElement).style.border = 'none';
	}

	onFormSubmit(): void {
		this.resetErrorCards();

		const { valid } = this.authForm;
		const { email, password } = this.authForm.value;
		this.authForm.markAllAsTouched();
		// to-do: add password regex when done dev
		if (valid) {
			this.isLoading$.next(true);
			this.authDispatch.CheckEmailAndPassword(email, password);
		}
	}

	goToChooseLoginMethodScreen() {
		this.currloginScreen.next(LoginScreen.CHOOSE_LOGIN_METHOD);
	}

	goToUseExistingAccountScreen() {
		this.currloginScreen.next(LoginScreen.USE_EXISTING_ACCOUNT);
	}

	createNewAccount() {
		this.resetFormFields();
		this.resetErrorCards();

		// if the Leap account is connected to a Closer business, before we onboard the current user we check that their Leap email is not already used otherwise they have to onboard by providing an email and password
		if (this.emailIsNotAvailable()) {
			this.showEmailIsNotAvailableError$.next(true);
			return;
		}

		this.onboardingService.goToConfirmationScreen();
	}

	private resetFormFields() {
		this.authForm.patchValue({
			[USER_CREDENTIALS.EMAIL]: '',
			[USER_CREDENTIALS.PASSWORD]: '',
		});
		this.emailFormControl.setErrors(null);
		this.passwordFormControl.setErrors(null);
		this.authForm.updateValueAndValidity();
	}

	private resetErrorCards() {
		this.userAccountBelongsToDifferentBusinessError$.next({
			hasError: false,
			invalidEmail: '',
		});
		this.showEmailIsNotAvailableError$.next(false);
	}

	private emailIsNotAvailable() {
		return !this.onboardingService.onboardingStatusResponse
			.leapEmailIsAvailable;
	}

	private getUserAccountBelongsToDifferentBusinessValidator(): ValidatorFn {
		return (control: FormControl): ValidationErrors | null => {
			const errorDetails =
				this.userAccountBelongsToDifferentBusinessError$.getValue();

			if (
				errorDetails.hasError &&
				control.value === errorDetails.invalidEmail
			) {
				return { emailFromDifferentAccount: true };
			} else {
				return null;
			}
		};
	}

	async setupAuthStateChangeListenerForOnboarding() {
		combineLatest([
			this.loginChecked$,
			this.loggedInUserEmail$,
			this.currentBusinessId$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(async ([loginChecked, email, currentBusinessId]) => {
				if (!loginChecked || !email || !currentBusinessId) return;

				this.userAccountBelongsToDifferentBusinessError$.next({
					hasError: false,
					invalidEmail: '',
				});

				const onboardingStatus =
					this.onboardingService.onboardingStatusResponse.leapOnboardingStatus;

				if (onboardingStatus === LeapOnboardingStatus.NotOnboarded) {
					// In this case the user can either login OR they can click signup to onboard the firm and their own account
					return await this.connect(email, currentBusinessId);
				}

				if (onboardingStatus === LeapOnboardingStatus.UserNotOnboarded) {
					// In this case the user can either login OR they can click signup to onboard their account (in this case the firm has already been onboarded).

					// Credentials have been checked so get the info from state for logged-in user so we can map the user
					// to LEAP user.
					if (
						currentBusinessId ===
						this.onboardingService.onboardingStatusResponse.closerBusinessId
					) {
						await this.connect(email, currentBusinessId);
					} else {
						// Mismatch between businessId of loggedInUser and onboardingStatusResponse means user belongs to different business than the one that is attached to the Leap account

						this.authDispatch.LogOut();
						this.userAccountBelongsToDifferentBusinessError$.next({
							hasError: true,
							invalidEmail: this.emailFormControl.value,
						});
						this.isLoading$.next(false);

						//this.snackbarService.openSnackBar({ text: 'The account belongs to a different business. Try logging again with a different account or click the "Connect" button to connect ', type: 'info', icon: ICON.CIRCLE_EXCLAMATION });
					}

					return;
				}
			});
	}

	private async connect(email: string, businessId: string) {
		this.isLoading$.next(true);
		await this.onboardingService.connect(email, businessId);
	}
}
